import {
  Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Avatar, Chip, Paper,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const RATING_BADGE_MAP = {
  1: { label: 'Poor',      bg: '#FEF2F2', color: '#EF4444', border: '#FCA5A5' },
  2: { label: 'Below Avg', bg: '#FFF7ED', color: '#F97316', border: '#FDBA74' },
  3: { label: 'Average',   bg: '#FFFBEB', color: '#F59E0B', border: '#FCD34D' },
  4: { label: 'Good',      bg: '#ECFDF5', color: '#10B981', border: '#6EE7B7' },
  5: { label: 'Excellent', bg: '#EEF2FF', color: '#6366F1', border: '#A5B4FC' },
};

const RANK_STYLES = {
  1: { bg: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)', Icon: EmojiEventsIcon },
  2: { bg: 'linear-gradient(135deg, #B0BEC5 0%, #90A4AE 100%)', Icon: WorkspacePremiumIcon },
  3: { bg: 'linear-gradient(135deg, #CD7F32 0%, #BF6B20 100%)', Icon: WorkspacePremiumIcon },
};

const AVATAR_COLORS = [
  '#FF5200', '#6366F1', '#10B981', '#F59E0B',
  '#EC4899', '#8B5CF6', '#14B8A6', '#F97316',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// Calendar-day boundary in IST, not a raw 24h bucket — a review from 11pm IST
// yesterday must read "Yesterday" even though under 24h have elapsed since.
function istDayNumber(d) {
  const shifted = new Date(d.getTime() + IST_OFFSET_MS);
  return Math.floor(shifted.getTime() / 86400000);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const days = istDayNumber(new Date()) - istDayNumber(new Date(dateStr));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function RankBadge({ rank }) {
  const style = RANK_STYLES[rank];
  if (style) {
    const { Icon } = style;
    return (
      <Box
        sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: style.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto',
        }}
      >
        <Icon sx={{ fontSize: 15, color: '#fff' }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 32, height: 32, borderRadius: '50%', bgcolor: '#F3F4F6',
        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto',
      }}
    >
      <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 800, fontSize: 12, color: '#6B7280' }}>
        {rank}
      </Typography>
    </Box>
  );
}

const HEAD_SX = {
  fontFamily: 'Quicksand', fontWeight: 700, fontSize: 10,
  color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8,
  bgcolor: '#FAFAFA', borderBottom: '1px solid rgba(0,0,0,0.06)', py: 1.5,
};

const CELL_SX = {
  fontFamily: 'Quicksand', fontWeight: 600, fontSize: 13,
  color: '#374151', borderBottom: '1px solid rgba(0,0,0,0.04)', py: 1.75,
};

// Keyframes defined once at module level, referenced by each row from the same scope
const ROW_KEYFRAME = {
  '@keyframes rowSlideIn': {
    from: { opacity: 0, transform: 'translateX(-8px)' },
    to:   { opacity: 1, transform: 'translateX(0)' },
  },
};

function LeaderboardRow({ row, currentUserId, index }) {
  const isMe = row._id === currentUserId;
  const badge = RATING_BADGE_MAP[Math.round(row.avgRatingGiven)] || RATING_BADGE_MAP[3];
  const initials = (row.userFullName || '?')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const delay = `${Math.min(index * 0.04, 0.4)}s`;

  return (
    <TableRow
      sx={{
        ...ROW_KEYFRAME,
        animation: `rowSlideIn 0.3s ease ${delay} both`,
        bgcolor: isMe ? 'rgba(255,82,0,0.04)' : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: isMe ? 'rgba(255,82,0,0.07)' : 'rgba(0,0,0,0.02)' },
      }}
    >
      <TableCell align="center" sx={CELL_SX}>
        <RankBadge rank={row.rank} />
      </TableCell>

      <TableCell sx={CELL_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={row.profileImage || undefined}
            sx={{
              width: 34, height: 34, flexShrink: 0,
              bgcolor: getAvatarColor(row.userFullName),
              fontSize: 12, fontWeight: 800, fontFamily: 'Quicksand',
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 700, fontSize: 13, color: '#1C1C2E', lineHeight: 1.25 }}>
              {row.userFullName}
            </Typography>
            {isMe && (
              <Chip
                label="You"
                size="small"
                sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#FF5200', color: '#fff', fontFamily: 'Quicksand', mt: 0.3 }}
              />
            )}
          </Box>
        </Box>
      </TableCell>

      <TableCell align="center" sx={CELL_SX}>
        <Typography
          component="span"
          sx={{ display: 'block', fontFamily: 'Quicksand', fontWeight: 900, fontSize: 20, color: '#FF5200', lineHeight: 1 }}
        >
          {row.totalReviews ?? 0}
        </Typography>
      </TableCell>

      <TableCell align="center" sx={CELL_SX}>
        <Chip
          label={`${row.avgRatingGiven ?? '—'} · ${badge.label}`}
          size="small"
          sx={{
            fontSize: 11, fontWeight: 700, fontFamily: 'Quicksand',
            bgcolor: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
          }}
        />
      </TableCell>

      <TableCell align="right" sx={{ ...CELL_SX, pr: 3, color: '#9CA3AF', fontSize: 12 }}>
        {formatDate(row.lastReviewDate)}
      </TableCell>
    </TableRow>
  );
}

export default function LeaderboardTable({ leaderboard, currentUserId }) {
  if (!leaderboard.length) return null;

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: 'Quicksand', fontWeight: 800, fontSize: 11,
          color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1.4, mb: 2,
        }}
      >
        Full Rankings
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)',
          overflowX: 'auto',   // horizontal scroll on small screens
          overflowY: 'hidden',
        }}
      >
        <Table sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...HEAD_SX, width: 64 }}>Rank</TableCell>
              <TableCell sx={HEAD_SX}>Reviewer</TableCell>
              <TableCell align="center" sx={{ ...HEAD_SX, width: 120 }}>Reviews</TableCell>
              <TableCell align="center" sx={HEAD_SX}>Avg Rating</TableCell>
              <TableCell align="right" sx={{ ...HEAD_SX, pr: 3 }}>Last Review</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {leaderboard.map((row, index) => (
              <LeaderboardRow
                key={row._id ?? index}
                row={row}
                currentUserId={currentUserId}
                index={index}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
